import React, {useEffect, useState, useRef, useMemo} from 'react';
import {Pagination, message} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {useSorterPlanQuery, SorterPlanFiltersType} from './hooks/useSorterPlanQuery';
import SorterPlanFilter from './components/SorterPlanFilter';
import SorterPlanColumns from './components/SorterPlanTableColumns';
import SorterPlanModal from './components/SorterPlanModal';
import {DELETE_SORTER_PLAN} from './graphql/mutations/deleteSorterPlan';
import {GET_SORTER_LISTING} from '../../wms/sorter/graphql/queries/getSorterListing';
import {useWarehouseContext} from '../../../contexts/WarehouseContext';
import {useWarehouseOptions} from '../../../hooks/useWarehouseOptions';
import {
    GET_SORTER_PLAN_WORK_WAREHOUSE_OPTIONS,
    GetSorterPlanWorkWarehouseOptionsData,
} from './graphql/queries/getSorterPlanWorkWarehouseOptions';

const SorterPlanPage: React.FC = () => {
    const {t} = useTranslation();
    const {currentWarehouse} = useWarehouseContext();
    
    // 初始过滤条件的默认值
    const SorterPlanFilterInitialValue: SorterPlanFiltersType = {
        warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : undefined,
        sorterType: undefined,
        status: undefined,
    };
    
    // 初始化过滤条件时带上当前仓库ID
    const initialFilters = useMemo(() => ({
        ...SorterPlanFilterInitialValue,
        warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : undefined
    }), [currentWarehouse?.id]);
    
    const [tempFilters, setTempFilters] = useState<SorterPlanFiltersType>(initialFilters);
    const [filters, setFilters] = useState<SorterPlanFiltersType>(initialFilters);
    const initializedRef = useRef(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null); // 改为只存储 ID

    // 查询方案列表
    const {data, totalElements, loading, refetch} = useSorterPlanQuery(filters, currentPage, pageSize);

    // 查询分拣机列表用于下拉选项（只查询当前仓库的分拣机）
    const {data: sorterData} = useQuery(GET_SORTER_LISTING, {
        variables: {
            filters: {
                warehouseId: currentWarehouse?.id,
                sorterType: filters.sorterType
            },
            pageRequest: {page: 1, size: 1000}
        },
        skip: !currentWarehouse?.id
    });

    const sorterOptions = useMemo(() => {
        if (!sorterData?.getSorterListing?.content) return [];
        return sorterData.getSorterListing.content.map((sorter: any) => ({
            value: sorter.id,
            label: `${sorter.sorterCode} - ${sorter.sorterName}`
        }));
    }, [sorterData]);

    // 创建分拣机 ID 到名称的映射
    const sorterMap = useMemo(() => {
        if (!sorterData?.getSorterListing?.content) return new Map<string, string>();
        const map = new Map<string, string>();
        sorterData.getSorterListing.content.forEach((sorter: any) => {
            map.set(sorter.id, `${sorter.sorterCode} - ${sorter.sorterName}`);
        });
        return map;
    }, [sorterData]);

    // 作业仓下拉：按当前 hub 的白名单配置取可选范围（无配置时只有当前 hub 自身）；名称映射用全量仓库，兼容历史方案的非 HUB 作业仓
    const {data: workWarehouseData} = useQuery<GetSorterPlanWorkWarehouseOptionsData>(
        GET_SORTER_PLAN_WORK_WAREHOUSE_OPTIONS,
        {
            variables: {warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : 0},
            skip: !currentWarehouse?.id,
            fetchPolicy: 'network-only',
        }
    );
    const {warehouseOptions: allWarehouseOptions} = useWarehouseOptions('ALL');

    const warehouseSelectOptions = useMemo(() => {
        const options = workWarehouseData?.getSorterPlanWorkWarehouseOptions || [];
        return options.map(w => ({value: String(w.id), label: w.name || String(w.id)}));
    }, [workWarehouseData]);

    const warehouseAllSelectOptions = useMemo(() => {
        return allWarehouseOptions.map(w => ({value: w.id, label: w.name}));
    }, [allWarehouseOptions]);

    // 创建仓库 ID 到名称的映射（列表回显用）
    const warehouseMap = useMemo(() => {
        const map = new Map<string, string>();
        allWarehouseOptions.forEach(w => map.set(w.id, w.name));
        return map;
    }, [allWarehouseOptions]);

    // 新增方案默认作业仓：当前仓在可选范围内则默认当前仓；可选项唯一时默认它
    const defaultWarehouseId = useMemo(() => {
        const currentId = currentWarehouse?.id ? String(currentWarehouse.id) : undefined;
        if (currentId && warehouseSelectOptions.some(o => o.value === currentId)) {
            return Number(currentId);
        }
        return warehouseSelectOptions.length === 1 ? Number(warehouseSelectOptions[0].value) : undefined;
    }, [warehouseSelectOptions, currentWarehouse?.id]);

    // 删除方案
    const [deleteSorterPlan] = useMutation(DELETE_SORTER_PLAN);

    // 当仓库切换时，更新过滤条件
    useEffect(() => {
        if (!initializedRef.current) {
            setTempFilters(initialFilters);
            setFilters(initialFilters);
            initializedRef.current = true;
        } else if (currentWarehouse?.id) {
            // 仓库切换时更新 warehouseId，保留其他过滤条件
            const newWarehouseId = Number(currentWarehouse.id);
            setTempFilters(prev => ({ ...prev, warehouseId: newWarehouseId }));
            setFilters(prev => ({ ...prev, warehouseId: newWarehouseId }));
            setCurrentPage(1);
        }
    }, [currentWarehouse?.id, initialFilters]);

    const handleResetFilters = () => {
        // 重置时保留当前仓库ID
        const resetFilters = {
            ...SorterPlanFilterInitialValue,
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : undefined
        };
        setTempFilters(resetFilters);
        setFilters(resetFilters);
        setCurrentPage(1);
    };

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    useEffect(() => {
        refetch();
    }, [filters, currentPage, pageSize]);

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
    };

    const handleAddSorterPlan = () => {
        setEditingId(null);
        setModalVisible(true);
    };

    const handleDelete = async (id: string) => {
        try {
            await deleteSorterPlan({
                variables: {id: parseInt(id)}
            });
            message.success(t('wms.sorterPlan.messages.deleteSuccess'));
            refetch();
        } catch (error: any) {
            console.error('Delete sorter plan error:', error);
            
            // 解析错误信息
            let errorMessage = t('wms.sorterPlan.messages.deleteFailed');
            
            // 优先检查 extensions.messageKey
            if (error?.graphQLErrors?.[0]?.extensions?.messageKey) {
                const messageKey = error.graphQLErrors[0].extensions.messageKey;
                errorMessage = t(messageKey) || messageKey;
            } else if (error?.graphQLErrors?.[0]?.message) {
                const backendMessage = error.graphQLErrors[0].message;
                // 检查是否是业务异常消息键
                if (backendMessage.includes('errors.business.sorterPlan.deleteInUse')) {
                    errorMessage = t('wms.sorterPlan.messages.deleteInUse');
                } else if (backendMessage.startsWith('errors.')) {
                    // 尝试翻译其他错误键
                    const messageKey = backendMessage.replace('[BUSINESS_ERROR] ', '').split(' ')[0];
                    const translatedMessage = t(messageKey);
                    if (translatedMessage !== messageKey) {
                        errorMessage = translatedMessage;
                    } else {
                        errorMessage = backendMessage;
                    }
                } else {
                    errorMessage = backendMessage;
                }
            } else if (error?.message) {
                errorMessage = error.message;
            }
            
            message.error(errorMessage);
        }
    };

    const handleModalCancel = () => {
        setModalVisible(false);
        setEditingId(null);
    };

    const handleModalSuccess = () => {
        setModalVisible(false);
        setEditingId(null);
        refetch();
    };

    return (
        <div className="bg-white rounded-lg shadow-sm p-6">
            {/* 筛选条件 */}
            <SorterPlanFilter
                filters={tempFilters}
                setFilters={setTempFilters}
                handleFilter={handleFilter}
                handleResetFilters={handleResetFilters}
                onAddSorterPlan={handleAddSorterPlan}
            />

            {/* 方案列表 */}
            <SorterPlanColumns
                sorterPlanData={data}
                loading={loading}
                onDelete={handleDelete}
                sorterMap={sorterMap}
                warehouseMap={warehouseMap}
            />

            {/* 分页 */}
            <div className="flex justify-between items-center mt-4">
                <div className="text-gray-500 text-sm">{t('wms.sorterPlan.pagination.total', {total: totalElements})}</div>
                <Pagination
                    current={currentPage}
                    total={totalElements}
                    pageSize={pageSize}
                    size="small"
                    showSizeChanger
                    onChange={handlePageChange}
                />
            </div>

            {/* 新增/编辑模态框 */}
            <SorterPlanModal
                visible={modalVisible}
                onCancel={handleModalCancel}
                onSuccess={handleModalSuccess}
                editingId={editingId}
                sorterOptions={sorterOptions}
                warehouseOptions={warehouseSelectOptions}
                allWarehouseOptions={warehouseAllSelectOptions}
                defaultWarehouseId={defaultWarehouseId}
            />
        </div>
    );
};

export default SorterPlanPage;
